import { useRef, useMemo } from "react";
import { FormInput } from "@/components/Inputs/FormInput";
import { OptionInput } from "@/components/Inputs/OptionInput";
import { PrimaryButton } from "@/components/Buttons/PrimaryButton";
import { SecondaryButton } from "@/components/Buttons/SecondaryButton";
import { Spacing } from "@/components/Spacing/Spacing";
import { useTranslations, useLocale } from "next-intl";
import { TermsConsentCheckbox } from "@/components/Legal/TermsConsentCheckbox";
import { DpaConsentCheckbox } from "@/components/Legal/DpaConsentCheckbox";
import { IconCircleCheck, IconCircleX } from "@tabler/icons-react";
import { IconText } from "@/components/IconText/IconText";
import { Link } from "react-router-dom";
import {
  getCountryDisplayNames,
  getCountryCodeFromName,
  getCountryNameFromCode,
} from "@/lib/utils/countries";
import { useCompleteSignupForm } from "./useCompleteSignupForm";

type CompleteSignupFormProps = {
  token: string;
  email: string;
  /**
   * The token's pending row carries an invite code: the person joins an
   * organisation whose DPA already stands, no company is created — so no DPA
   * box, and the terms+privacy map alone (W9 1-S, audit lens-7 F3); and no
   * company block either — the backend reads neither country nor customer
   * type nor tax ID for an invite-coded token (lens-8 F7), so, as on the
   * invite form, they are not asked. Read by the parent from the
   * registration-link response (`invite`), false when the backend does not say.
   */
  invite?: boolean;
};

/**
 * Full signup form (step 3). Email is read-only — it comes from the validated
 * magic-link token. No language picker (locale is forced to the token's
 * language by the parent) and no invite toggle: the lane is the token's.
 */
export default function CompleteSignupForm({
  token,
  email,
  invite = false,
}: CompleteSignupFormProps) {
  const t = useTranslations("register");
  const locale = useLocale();

  const {
    formData,
    formErrors,
    passwordValidation,
    isLoading,
    handleInputChange,
    handleSubmit,
    rateLimited,
    termsAccepted,
    termsInvalid,
    dpaAccepted,
    dpaInvalid,
    handleTermsChange,
    handleDpaChange,
    termsRef,
    dpaRef,
  } = useCompleteSignupForm(token, invite);

  const countryInputRef = useRef<HTMLInputElement>(null);
  const countryOptions = useMemo(() => getCountryDisplayNames(locale), [locale]);
  const countryDisplayValue = useMemo(() => {
    if (!formData.country) return "";
    return getCountryNameFromCode(formData.country, locale);
  }, [formData.country, locale]);

  return (
    <form onSubmit={handleSubmit} className="w-full">
      {/* Email — read-only, proven via the magic link */}
      <FormInput
        key="email"
        id="email"
        value={email}
        label={t("email")}
        onChangeAction={() => {}}
        disabled={true}
      />

      <Spacing height="h-6" />

      {/* The company block — only where a COMPANY is created. On the invite
          lane the person joins an organisation that already has its country
          and tax ID, and the backend reads none of these for an invite-coded
          token (lens-8 F7) — hidden, as the invite form hides them. */}
      {!invite && (
        <>
          {/* Veltric is B2B-only (Kanzlei 7.1): every new account is a COMPANY,
              so there is no account-type choice left — only this note. */}
          <div>
            <p className="body-2-regular text-secondary" data-testid="auth-business-only-hint">
              {t("businessOnlyHint")}
            </p>
            {formErrors.customerType && (
              <p className="body-2-regular text-error mt-1">{formErrors.customerType}</p>
            )}
          </div>

          {/* Country / Organisation location */}
          <div className="mt-6 w-full">
            <OptionInput
              inputRef={countryInputRef}
              options={countryOptions}
              sortOptions={true}
              defaultValue={countryDisplayValue}
              required={false}
              label={t("companyLocation")}
              error={formErrors.country}
              testId="auth-complete-country"
              setParent={(displayName) => {
                const code = getCountryCodeFromName(displayName, locale);
                if (code) handleInputChange("country", code);
                else if (displayName === "") handleInputChange("country", "");
              }}
            />
          </div>

          {/* Tax ID (optional: clubs, Kleinunternehmer, freelancers have none) — only for COMPANY */}
          {formData.customerType === "COMPANY" && (
            <div className="mt-6">
              <FormInput
                key="taxId"
                id="taxId"
                value={formData.taxId}
                label={t("taxIdOptional")}
                onChangeAction={(value) => handleInputChange("taxId", value)}
                error={formErrors.taxId}
              />
              <p
                className="body-2-regular text-secondary mt-1"
                data-testid="auth-tax-id-optional-hint"
              >
                {t("taxIdOptionalHint")}
              </p>
            </div>
          )}
        </>
      )}

      <div className="border-t outline-quaternary my-8" />

      {/* Name fields */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 w-full">
        {["firstName", "lastName"].map((id) => (
          <FormInput
            autoComplete={true}
            key={id}
            id={id}
            value={formData[id as keyof typeof formData]}
            label={t(id)}
            onChangeAction={(value) =>
              handleInputChange(id as "firstName" | "lastName", value)
            }
            error={formErrors[id]}
            className="flex-1"
          />
        ))}
      </div>

      {/* Password fields */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 w-full mt-6">
        {["password", "confirmPassword"].map((id) => (
          <FormInput
            key={id}
            id={id}
            type="password"
            value={formData[id as keyof typeof formData]}
            label={t(id)}
            onChangeAction={(value) =>
              handleInputChange(id as "password" | "confirmPassword", value)
            }
            error={formErrors[id]}
          />
        ))}
      </div>

      <Spacing height="h-4" />

      {/* Password validation */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
        {["length", "uppercase", "lowercase", "specialChar"].map((id) => (
          <IconText
            key={id}
            isValid={passwordValidation[id as keyof typeof passwordValidation]}
            text={t(id)}
            defaultIcon={<IconCircleX size={16} stroke={2} />}
            successIcon={<IconCircleCheck size={16} stroke={2} />}
          />
        ))}
      </div>

      <Spacing height="h-6" />

      {/* Explicit ticks (W9 1-S): the terms+privacy sentence on every lane;
          the separate DPA only where a COMPANY is created — not on the invite
          lane, where the person joins an organisation whose DPA already
          stands (⚑A1, lens-7 F3). Validation happens on the click, not by
          disabling the button: the first box left unticked is marked,
          explained and focused. */}
      <TermsConsentCheckbox
        ref={termsRef}
        isChecked={termsAccepted}
        isInvalid={termsInvalid}
        onChangeAction={handleTermsChange}
        testId="auth-complete-terms"
      />

      {!invite && (
        <>
          <Spacing height="h-4" />
          <DpaConsentCheckbox
            ref={dpaRef}
            isChecked={dpaAccepted}
            isInvalid={dpaInvalid}
            onChangeAction={handleDpaChange}
            testId="auth-complete-dpa"
          />
        </>
      )}

      {formErrors.consent && (
        <p
          className="body-2-regular text-error mt-2"
          role="alert"
          data-testid="auth-complete-consent-error"
        >
          {formErrors.consent}
        </p>
      )}

      <Spacing height="h-8" />

      <PrimaryButton
        type="submit"
        text={t("createAccount")}
        size="small"
        disabled={isLoading || rateLimited}
        loading={isLoading}
      />

      <Spacing height="h-3" />

      <Link to="/auth/login">
        <SecondaryButton text={t("alreadyHaveAccount")} size="small" />
      </Link>
    </form>
  );
}
